import React, { useState } from 'react'
import { PatientHistoryType } from 'src/graphql.generated'
import { useIntakeNotePrefill } from '../../../../api/usePatientHistoryPrefill'
import { useSubmitPatientHistory } from '../../../../api/useSubmitPatientHistory'
import { useSelfPay } from '../useSelfPay'
import { BillingCode } from '../billingCodes'
import BillingCodesSelect from '../BillingCodesSelect'
import IntakeTrackSelect from '../IntakeTrackSelect'
import PrefillLoading from '../PrefillLoading'
import {
  CheckboxGroup,
  FieldGrid,
  FieldSection,
  FormActions,
  LabeledCheckbox,
  LabeledDate,
  LabeledNumber,
  LabeledRadioGroup,
  LabeledText,
  LabeledValue,
  MissingFields,
  todayIso,
  useCheckboxGroup,
  useRequiredFields,
} from '../../../fieldPrimitives'
import { EMPLOYMENT_OPTIONS, HEALTH_HISTORY_OPTIONS } from '../../historyOptions'

type Prefill = ReturnType<typeof useIntakeNotePrefill>['prefill']

const IntakeNoteForm = ({ uid, prefill }: { uid: string; prefill: Prefill }) => {
  const [date, setDate] = useState(todayIso())
  const [ineligible, setIneligible] = useState(false)
  const [identifyingInfo, setIdentifyingInfo] = useState('')
  const [expectations, setExpectations] = useState('')
  const [employmentStatus, setEmploymentStatus] = useState<(typeof EMPLOYMENT_OPTIONS)[number]['value'] | ''>(
    prefill?.EMPLOYMENT?.STATUS ?? ''
  )
  const [healthHistory, toggleHealthHistory] = useCheckboxGroup(
    HEALTH_HISTORY_OPTIONS,
    Object.fromEntries(
      HEALTH_HISTORY_OPTIONS.map(({ key }) => [
        key,
        Boolean(prefill?.HEALTH_HISTORY?.[key as keyof NonNullable<Prefill>['HEALTH_HISTORY']]),
      ])
    )
  )
  const [timeline, setTimeline] = useState('')
  const [mddEpisodes, setMddEpisodes] = useState<number | ''>('')
  const [suicideRisk, setSuicideRisk] = useState('')
  const [billingCodes, setBillingCodes] = useState<BillingCode[]>([])
  const [trackKey, setTrackKey] = useState('')
  const [startingDateConfirmed, setStartingDateConfirmed] = useState(false)
  const [reviewedConfidentiality, setReviewedConfidentiality] = useState(false)
  const [verbalConsent, setVerbalConsent] = useState(false)
  const [createQuickNote, setCreateQuickNote] = useState(false)
  const { submit, submitting, error } = useSubmitPatientHistory(uid)
  const { isSelfPay } = useSelfPay(uid)

  const onSubmit = async () => {
    if (!required.attempt()) {
      return
    }

    await submit({
      date,
      type: PatientHistoryType.IntakeNote,
      value: {
        __version: 3,
        PARTICIPANT_INELIGIBLE: ineligible,
        CONSENT: {
          REVIEWED_CONFIDENTIALITY_AND_EXCEPTIONS: reviewedConfidentiality,
          VERBAL_CONSENT_FOR_TREATMENT: verbalConsent,
        },
        HEALTH_HISTORY: healthHistory,
        EMPLOYMENT: { STATUS: employmentStatus },
        TIMELINE_OF_SYMPTOMS_AND_TREATMENT: timeline,
        IDENTIFYING_INFO: identifyingInfo,
        EXPECTATIONS_AND_INTERESTS: expectations,
        SUICIDE_RISK: suicideRisk,
        OBSTACLES: prefill?.OBSTACLES ?? null,
        STARTING_DATE_CONFIRMED: startingDateConfirmed,
        CREATE_QUICK_NOTE: createQuickNote,
        MAJOR_DEPRESSION_EPISODES: mddEpisodes === '' ? 0 : mddEpisodes,
        BILLING_CODES: billingCodes,
        ...(trackKey ? { trackKey } : null),
      },
    })
  }

  // As in the original's Yup schema, marking the participant ineligible waives only the starting date confirmation.
  const required = useRequiredFields({
    'Date of event': Boolean(date),
    'Identifying Information': Boolean(identifyingInfo.trim()),
    'Presenting Problem / Reason for Referral': Boolean(expectations.trim()),
    'Clinical formulation and timeline of symptoms': Boolean(timeline.trim()),
    'Previous MDD episodes': mddEpisodes !== '',
    'Suicide Risk Assessment': Boolean(suicideRisk.trim()),
    ...(!isSelfPay && { 'Billing codes': billingCodes.length > 0 }),
    ...(!ineligible && { 'The participant is eligible for the program': startingDateConfirmed }),
    'Reviewed confidentiality and exceptions': reviewedConfidentiality,
    'Therapist obtained verbal consent for treatment': verbalConsent,
    'Create quick note': createQuickNote,
  })

  return (
    <FieldSection>
      <FieldGrid>
        <LabeledDate
          label="Date of event"
          value={date}
          onChange={setDate}
          required
          error={required.error('Date of event')}
        />
        <IntakeTrackSelect value={trackKey} onChange={setTrackKey} />
      </FieldGrid>
      <LabeledCheckbox
        label="The participant is not eligible for the program"
        checked={ineligible}
        onChange={setIneligible}
        helperText="The participant is not eligible for the treatment. The rest of the note still has to be filled in."
      />

      <FieldSection title="Identifying information and treatment expectations">
        <LabeledText
          label="Identifying Information"
          value={identifyingInfo}
          onChange={setIdentifyingInfo}
          required
          error={required.error('Identifying Information')}
          multiline
        />
        <LabeledText
          label="Presenting Problem / Reason for Referral"
          value={expectations}
          onChange={setExpectations}
          helperText="Include treatment expectations"
          required
          error={required.error('Presenting Problem / Reason for Referral')}
          multiline
        />
      </FieldSection>

      <FieldSection title="Clinical Assessment">
        <LabeledRadioGroup
          label="Are you currently…"
          value={employmentStatus}
          onChange={setEmploymentStatus}
          options={[...EMPLOYMENT_OPTIONS]}
        />
        <CheckboxGroup
          title="Health History"
          options={HEALTH_HISTORY_OPTIONS}
          values={healthHistory}
          onChange={toggleHealthHistory}
        />
        <LabeledText
          label="Clinical formulation and timeline of symptoms"
          value={timeline}
          onChange={setTimeline}
          helperText="Symptom diagnosis justification and functioning"
          required
          error={required.error('Clinical formulation and timeline of symptoms')}
          multiline
        />
        <FieldGrid>
          <LabeledNumber
            label="Previous MDD episodes"
            value={mddEpisodes}
            onChange={setMddEpisodes}
            min={0}
            required
            error={required.error('Previous MDD episodes')}
          />
        </FieldGrid>
        <LabeledText
          label="Suicide Risk Assessment"
          value={suicideRisk}
          onChange={setSuicideRisk}
          helperText="Previous suicide attempts, hospitalizations, current SI, intent, protective factors and safety plan"
          required
          error={required.error('Suicide Risk Assessment')}
          multiline
        />
      </FieldSection>

      <FieldSection title="Billing code and diagnosis">
        <BillingCodesSelect
          value={billingCodes}
          onChange={setBillingCodes}
          required={!isSelfPay}
          error={required.error('Billing codes')}
        />
      </FieldSection>

      <LabeledValue
        label="Is there something that you want your therapist to know?"
        value={prefill?.OBSTACLES || '-'}
      />

      <FieldSection title="Confirmation and Next steps">
        <LabeledCheckbox
          label="The participant is eligible for the program"
          checked={startingDateConfirmed}
          onChange={setStartingDateConfirmed}
          helperText="Remember to agree on a starting date with the participant"
          required={!ineligible}
          error={required.error('The participant is eligible for the program')}
        />
        <LabeledCheckbox
          label="Reviewed confidentiality and exceptions"
          checked={reviewedConfidentiality}
          onChange={setReviewedConfidentiality}
          helperText="Review confidentiality and reporting requirements for suspected or known abuse of minors, elderly or concern about imminent harm to self or others."
          required
          error={required.error('Reviewed confidentiality and exceptions')}
        />
        <LabeledCheckbox
          label="Therapist obtained verbal consent for treatment"
          checked={verbalConsent}
          onChange={setVerbalConsent}
          required
          error={required.error('Therapist obtained verbal consent for treatment')}
        />
        <LabeledCheckbox
          label="Create quick note"
          checked={createQuickNote}
          onChange={setCreateQuickNote}
          helperText="Add stressors, goals, and other talking points to be used during treatment"
          required
          error={required.error('Create quick note')}
        />
      </FieldSection>

      <MissingFields labels={required.missing} />
      <FormActions onSubmit={onSubmit} disabled={false} submitting={submitting} error={error} />
    </FieldSection>
  )
}

const IntakeNoteFields = ({ uid }: { uid: string }) => {
  const { loading, prefill } = useIntakeNotePrefill(uid)

  return loading ? <PrefillLoading /> : <IntakeNoteForm uid={uid} prefill={prefill} />
}

export default IntakeNoteFields
